import React from 'react';

export const ProcessingAnimation: React.FC = () => {
  // Optional: Add a bit of randomness to animation timing
  const randomDelay = (base: number, index: number, offset: number) =>
    `${(base + Math.random() * offset + index * 0.35).toFixed(2)}s`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 2000 1200"
      className="w-full min-h-[800px]"
    >
      <defs>
        <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 0.6 }} />
          <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0 }} />
        </radialGradient>
      </defs>

      {/* Main Processing Structure - Increased scale */}
      <g transform="translate(1000,600)">
        <g transform="scale(5)">
          <g transform="skewY(-15)">

            {/* Main rectangular frame */}
            <path
              d="M-150,-60 L150,-60 L150,60 L-150,60 Z"
              fill="#059669"
              fillOpacity="0.1"
              stroke="#34d399"
              strokeWidth="1"
            />

            {/* Subdivisions */}
            <g stroke="#34d399" strokeWidth="0.5" opacity="0.2">
              <path d="M-150,-40 L150,-40"/>
              <path d="M-150,-20 L150,-20"/>
              <path d="M-150,0 L150,0"/>
              <path d="M-150,20 L150,20"/>
              <path d="M-150,40 L150,40"/>
              <path d="M-100,-60 L-100,60"/>
              <path d="M0,-60 L0,60"/>
              <path d="M100,-60 L100,60"/>
            </g>

            {/* Left Hexagon */}
            <g transform="translate(-100,0)">
              <path
                d="M0,-45 L39,-22.5 L39,22.5 L0,45 L-39,22.5 L-39,-22.5 Z"
                fill="#10b981" fillOpacity="0.1" stroke="#34d399"
              >
                <animate
                  attributeName="fillOpacity"
                  values="0.1;0.3;0.1"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>
              <g>
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0"
                  to="360"
                  dur="10s"
                  repeatCount="indefinite"
                />
                <circle cx="0" cy="-20" r="3" fill="#10b981"/>
                <circle cx="17.3" cy="10" r="3" fill="#10b981"/>
                <circle cx="-17.3" cy="10" r="3" fill="#10b981"/>
              </g>
              <circle cx="0" cy="0" r="15" fill="#10b981">
                <animate
                  attributeName="r"
                  values="15;18;15"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>

            {/* Center Hexagon */}
            <g>
              <path
                d="M0,-50 L43.3,-25 L43.3,25 L0,50 L-43.3,25 L-43.3,-25 Z"
                fill="#34d399" fillOpacity="0.1" stroke="#34d399"
              >
                <animate
                  attributeName="fillOpacity"
                  values="0.1;0.3;0.1"
                  dur="2s"
                  begin="0.6s"
                  repeatCount="indefinite"
                />
              </path>
              <g>
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="360"
                  to="0"
                  dur="8s"
                  repeatCount="indefinite"
                />
                <path
                  d="M0,-30 L26,-15 L26,15 L0,30 L-26,15 L-26,-15 Z"
                  fill="none"
                  stroke="#34d399"
                  strokeWidth="1"
                />
              </g>
              <circle cx="0" cy="0" r="20" fill="#34d399">
                <animate
                  attributeName="r"
                  values="20;23;20"
                  dur="2s"
                  begin="0.6s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>

            {/* Right Hexagon */}
            <g transform="translate(100,0)">
              <path
                d="M0,-45 L39,-22.5 L39,22.5 L0,45 L-39,22.5 L-39,-22.5 Z"
                fill="#059669" fillOpacity="0.1" stroke="#34d399"
              >
                <animate
                  attributeName="fillOpacity"
                  values="0.1;0.3;0.1"
                  dur="2s"
                  begin="1.2s"
                  repeatCount="indefinite"
                />
              </path>
              <g>
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0"
                  to="-360"
                  dur="12s"
                  repeatCount="indefinite"
                />
                <circle cx="0" cy="-25" r="3" fill="#059669"/>
                <circle cx="21.7" cy="12.5" r="3" fill="#059669"/>
                <circle cx="-21.7" cy="12.5" r="3" fill="#059669"/>
              </g>
              <circle cx="0" cy="0" r="15" fill="#059669">
                <animate
                  attributeName="r"
                  values="15;18;15"
                  dur="2s"
                  begin="1.2s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>

            {/* Horizontal Pulse Lines */}
            <g>
              <path
                d="M-80,0 C-65,0 -35,0 -20,0"
                stroke="#34d399"
                strokeWidth="2"
                fill="none"
              >
                <animate
                  attributeName="opacity"
                  values="0.2;0.8;0.2"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>
              <path
                d="M20,0 C35,0 65,0 80,0"
                stroke="#34d399"
                strokeWidth="2"
                fill="none"
              >
                <animate
                  attributeName="opacity"
                  values="0.2;0.8;0.2"
                  dur="2s"
                  begin="1s"
                  repeatCount="indefinite"
                />
              </path>
            </g>

            {/* Legacy traveling circles */}
            <g>
              <circle cx="-100" cy="0" r="4" fill="#10b981">
                <animate
                  attributeName="cx"
                  values="-100;100"
                  dur="3s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="1;0;1"
                  dur="3s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle cx="-100" cy="0" r="4" fill="#10b981">
                <animate
                  attributeName="cx"
                  values="-100;100"
                  dur="3s"
                  begin="1.5s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="1;0;1"
                  dur="3s"
                  begin="1.5s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>

            {/* New flows with randomness */}
            <g>
              {Array.from({ length: 8 }).map((_, i) => (
                <circle
                  key={i}
                  cx="-180"
                  cy={Math.random() * 40 - 20} // random Y within -20..20
                  r={Math.random() * 3 + 2}    // random radius 2..5
                  fill="#10b981"
                >
                  <animate
                    attributeName="cx"
                    from="-180"
                    to="180"
                    dur="3s"
                    begin={randomDelay(0.2, i, 1)}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="1;0;1"
                    dur="3s"
                    begin={randomDelay(0.2, i, 1)}
                    repeatCount="indefinite"
                  />
                </circle>
              ))}
            </g>

            {/* Rotating ring */}
            <g>
              <circle
                cx="0"
                cy="0"
                r="80"
                fill="none"
                stroke="#34d399"
                strokeWidth="2"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 0 0"
                  to="360 0 0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>

            {/* Pulsing center circle */}
            <circle
              cx="0"
              cy="0"
              r="12"
              fill="#10b981"
            >
              <animate
                attributeName="r"
                values="12;18;12"
                dur="1s"
                repeatCount="indefinite"
              />
            </circle>
          </g>

          {/* Dimmed side panels */}
          <path
            d="M-150,-60 L-150,60 L-130,80 L-130,-40 Z"
            fill="#059669" fillOpacity="0.05"
            stroke="#34d399" strokeWidth="1"
          />
          <path
            d="M150,-60 L150,60 L130,80 L130,-40 Z"
            fill="#059669" fillOpacity="0.05"
            stroke="#34d399" strokeWidth="1"
          />
        </g>
      </g>
    </svg>
  );
};
